import { useMemo, useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import PageHeader from "@/components/common/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api from "@/lib/api";

const categories = ["Frontend", "Backend", "Database", "Cloud", "Mobile", "DevOps", "Business", "Customer", "Sales", "Design", "Management"];
const blank = { skillName: "", skillCategory: "Frontend", proficiencyPercentage: 75, iconUrl: "fa-brands fa-react", displayOrder: 1 };

const ICON_OPTIONS = [
  { value: "fa-brands fa-linkedin", label: "LinkedIn" },
  { value: "fa-brands fa-github", label: "GitHub" },
  { value: "fa-brands fa-twitter", label: "Twitter" },
  { value: "fa-brands fa-instagram", label: "Instagram" },
  { value: "fa-brands fa-facebook", label: "Facebook" },
  { value: "fa-brands fa-youtube", label: "YouTube" },
  { value: "fa-brands fa-dribbble", label: "Dribbble" },
  { value: "fa-brands fa-behance", label: "Behance" },
  { value: "fa-solid fa-envelope", label: "Email" },
  { value: "fa-solid fa-link", label: "Link" },
  { value: "fa-solid fa-code", label: "Code" },
  { value: "fa-solid fa-laptop-code", label: "Laptop Code" },
  { value: "fa-solid fa-database", label: "Database" },
  { value: "fa-solid fa-server", label: "Server" },
  { value: "fa-brands fa-react", label: "React" },
  { value: "fa-brands fa-node-js", label: "Node.js" },
  { value: "fa-brands fa-python", label: "Python" },
  { value: "fa-brands fa-js", label: "JavaScript" },
  { value: "fa-brands fa-html5", label: "HTML5" },
  { value: "fa-brands fa-css3-alt", label: "CSS3" },
  { value: "fa-solid fa-pen-nib", label: "Design" },
  { value: "fa-solid fa-camera", label: "Photography" },
  { value: "fa-solid fa-gamepad", label: "Gaming" },
  { value: "fa-solid fa-music", label: "Music" },
  { value: "fa-solid fa-plane", label: "Travel" },
  { value: "fa-solid fa-book", label: "Reading" },
  { value: "fa-solid fa-dumbbell", label: "Fitness" },
  { value: "fa-solid fa-heart", label: "Heart" },
  { value: "fa-solid fa-star", label: "Star" },
  { value: "fa-solid fa-briefcase", label: "Briefcase" },
  { value: "fa-solid fa-chart-line", label: "Chart Line" },
  { value: "fa-solid fa-chart-pie", label: "Chart Pie" },
  { value: "fa-solid fa-users", label: "Users" },
  { value: "fa-solid fa-user-tie", label: "User Tie" },
  { value: "fa-solid fa-headset", label: "Support" },
  { value: "fa-solid fa-sack-dollar", label: "Money" },
  { value: "fa-solid fa-handshake", label: "Handshake" },
  { value: "fa-solid fa-bullhorn", label: "Marketing" },
  { value: "fa-solid fa-person-running", label: "Running" },
  { value: "fa-solid fa-person-swimming", label: "Swimming" },
  { value: "fa-solid fa-person-biking", label: "Biking" },
  { value: "fa-solid fa-mountain", label: "Hiking/Outdoors" },
  { value: "fa-solid fa-campground", label: "Camping" },
  { value: "fa-solid fa-futbol", label: "Soccer" },
  { value: "fa-solid fa-basketball", label: "Basketball" },
  { value: "fa-solid fa-medal", label: "Medal" },
  { value: "fa-solid fa-microphone", label: "Public Speaking" },
  { value: "fa-solid fa-chalkboard-user", label: "Teaching" },
  { value: "fa-solid fa-people-group", label: "Audience" },
  { value: "fa-solid fa-comments", label: "Discussion" },
  { value: "fa-solid fa-podcast", label: "Podcast" },
  { value: "fa-solid fa-pen-fancy", label: "Writing" }
];

const categoryAccent = {
  Frontend: "text-chart-1",
  Backend: "text-chart-2",
  Database: "text-chart-3",
  Cloud: "text-chart-4",
  Mobile: "text-chart-5",
  DevOps: "text-primary",
  Business: "text-blue-500",
  Customer: "text-green-500",
  Sales: "text-yellow-500",
  Design: "text-purple-500",
  Management: "text-orange-500"
};

export default function TechnicalSkills() {
  const [items, setItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSkills = async () => {
    try {
      setLoading(true);
      const res = await api.get("/TechnicalSkill");
      if (res.success) {
        setItems(res.data || []);
      } else {
        toast.error(res.message || "Failed to fetch skills");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(blank);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm(item);
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.skillName) {
      toast.error("Skill name is required.");
      return;
    }
    
    try {
      if (editing) {
        const res = await api.put(`/TechnicalSkill/${editing.id}`, form);
        if (res.success) {
          toast.success("Skill updated.");
          fetchSkills();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Update failed");
        }
      } else {
        const res = await api.post("/TechnicalSkill", form);
        if (res.success) {
          toast.success("Skill added.");
          fetchSkills();
          setModalOpen(false);
        } else {
          toast.error(res.message || "Creation failed");
        }
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await api.delete(`/TechnicalSkill/${toDelete.id}`);
      if (res.success) {
        toast.success("Skill removed.");
        fetchSkills();
      } else {
        toast.error(res.message || "Failed to delete");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred");
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Technical Skills"
        description="Curate your stack and the depth at which you operate."
        testId="skills"
        action={openCreate}
        actionLabel="Add Skill"
        actionIcon={Plus}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((skill) => {
          const isFA = skill.iconUrl && (skill.iconUrl.includes("fa-") || skill.iconUrl.includes("fa "));
          const Icon = !isFA ? (Icons[skill.iconUrl] || Icons.Code2) : null;
          const accent = categoryAccent[skill.skillCategory] || "text-primary";
          return (
            <Card
              key={skill.id}
              data-testid={`skill-card-${skill.id}`}
              className="p-5 group hover:border-foreground/20 transition-colors relative"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`h-10 w-10 rounded-lg bg-muted flex items-center justify-center ${accent}`}>
                  {isFA ? <i className={`${skill.iconUrl} text-xl`}></i> : <Icon className="h-5 w-5" strokeWidth={1.5} />}
                </div>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(skill)} data-testid={`skill-edit-${skill.id}`}>
                    <Pencil className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive"
                    onClick={() => { setToDelete(skill); setConfirmOpen(true); }}
                    data-testid={`skill-delete-${skill.id}`}
                  >
                    <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
              <h3 className="font-display text-base font-semibold truncate">{skill.skillName}</h3>
              <Badge variant="secondary" className="mt-1 text-[10px]">{skill.skillCategory}</Badge>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Proficiency</span>
                  <span className="font-mono text-xs font-semibold">{skill.proficiencyPercentage}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-chart-5 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${skill.proficiencyPercentage}%` }}
                  />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="skill-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit skill" : "Add skill"}</DialogTitle>
            <DialogDescription>Curate a skill and the depth at which you operate.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Skill Name</Label>
              <Input
                data-testid="skill-name"
                value={form.skillName}
                onChange={(e) => setForm({ ...form, skillName: e.target.value })}
                placeholder="React"
                className="mt-1.5"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Category</Label>
                <Select value={form.skillCategory} onValueChange={(v) => setForm({ ...form, skillCategory: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="skill-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Icon</Label>
                <Select value={form.iconUrl} onValueChange={(v) => setForm({ ...form, iconUrl: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="skill-icon">
                    <SelectValue placeholder="Select an icon" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {ICON_OPTIONS.map((icon) => (
                      <SelectItem key={icon.value} value={icon.value}>
                        <div className="flex items-center gap-2">
                          <i className={`${icon.value} w-4 text-center text-sm`}></i>
                          <span>{icon.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Proficiency</Label>
                <span className="font-mono text-sm font-semibold">{form.proficiencyPercentage}%</span>
              </div>
              <Slider
                value={[form.proficiencyPercentage]}
                onValueChange={(v) => setForm({ ...form, proficiencyPercentage: v[0] })}
                max={100}
                step={1}
                data-testid="skill-proficiency"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="skill-save">{editing ? "Save changes" : "Add skill"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete skill?"
        description="This will remove the skill from your portfolio grid."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
}
